import { MOST_COMMON_PASSWORDS } from './commonPasswords.js';

/**
 * The rules for a new password, shared by the forms (which check as the
 * person types, and show how strong it is) and the server (which checks
 * again, against the whole list of common passwords).
 *
 * A new password needs at least 8 characters, with at least one uppercase
 * letter, one number and one special character: the four requirements below.
 * Asking for kinds of characters is IT Spar's choice (October 2026), although
 * NIST SP 800-63B advises against it. On top of the requirements, these are
 * refused: a password built from the person's name or email address, one
 * that repeats itself, and one that is, or is built from, one of the most
 * common. The rules apply when a password is chosen; one chosen under
 * earlier rules still signs in.
 */

/** Counted in characters as people see them (code points), after NFC. */
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;
/** A password that meets every requirement is strong from this many characters, with lower case letters too. */
export const STRONG_PASSWORD_LENGTH = 12;

/** How long the link that confirms a sign-up works. The emails and pages say "24 hours". */
export const SIGNUP_LINK_TTL_HOURS = 24;
/** How long a sign-up that was never confirmed is kept before it is deleted. */
export const PENDING_SIGNUP_KEPT_DAYS = 7;
/** How long a link to choose a new password works. The emails and pages say "30 minutes". */
export const RESET_LINK_TTL_MINUTES = 30;
/** How long a link to add a password to an account works. The emails and pages say "an hour". */
export const ADD_PASSWORD_LINK_TTL_MINUTES = 60;

/** The same password typed on two keyboards can arrive as different bytes ("é" as one character or two); NFC makes them one. */
export function normalizePassword(password: string): string {
  return password.normalize('NFC');
}

/** Characters as people count them: an emoji or an accented letter is one. */
export function passwordLength(password: string): number {
  return Array.from(normalizePassword(password)).length;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A rough check that something is an email address, before asking the server: name@example.com. */
export function looksLikeEmail(value: string): boolean {
  const email = value.trim();
  return email.length <= 254 && EMAIL.test(email);
}

/** Who is choosing the password: their email address and name, and the app's name, which it must not be built from. */
export interface PasswordAbout {
  email?: string;
  name?: string;
  appName?: string;
}

export type PasswordRequirementId = 'length' | 'uppercase' | 'number' | 'special';

/** One of the four requirements, as the forms list it, and whether a password meets it. */
export interface PasswordRequirement {
  id: PasswordRequirementId;
  /** As the hint names it: "8+ characters". */
  label: string;
  met: boolean;
}

/** An uppercase letter of any script: "A", and "É" too. */
const UPPERCASE = /\p{Lu}/u;
const LOWERCASE = /\p{Ll}/u;
/** A digit of any script: "7", and "७" too. */
const DIGIT = /\p{Nd}/u;
/**
 * Neither a letter nor a number: "!", "#", a space, an emoji. The marks that
 * belong to a letter (an accent, a Hindi vowel sign) are part of it, not
 * special characters.
 */
const SPECIAL = /[^\p{L}\p{M}\p{N}]/u;

const REQUIREMENTS: ReadonlyArray<{ id: PasswordRequirementId; label: string; met: (password: string) => boolean }> = [
  { id: 'length', label: `${MIN_PASSWORD_LENGTH}+ characters`, met: (p) => passwordLength(p) >= MIN_PASSWORD_LENGTH },
  { id: 'uppercase', label: '1 uppercase letter', met: (p) => UPPERCASE.test(p) },
  { id: 'number', label: '1 number', met: (p) => DIGIT.test(p) },
  { id: 'special', label: '1 special character', met: (p) => SPECIAL.test(p) },
];

/** The four requirements, in the order the hint names them, and which of them `password` meets. */
export function passwordRequirements(password: string): PasswordRequirement[] {
  const typed = normalizePassword(password);
  return REQUIREMENTS.map(({ id, label, met }) => ({ id, label, met: met(typed) }));
}

export type PasswordProblem = 'too_short' | 'too_long' | 'needs_uppercase' | 'needs_number' | 'needs_special' | 'personal' | 'repetitive' | 'common';

/** The problem a password has when it misses a requirement. */
const MISSING: Record<PasswordRequirementId, Exclude<PasswordProblem, 'common'>> = {
  length: 'too_short',
  uppercase: 'needs_uppercase',
  number: 'needs_number',
  special: 'needs_special',
};

/** What each problem says to the person choosing the password. */
export const PASSWORD_PROBLEM_TEXT: Record<PasswordProblem, string> = {
  too_short: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
  too_long: `Use ${MAX_PASSWORD_LENGTH} characters or fewer.`,
  needs_uppercase: 'Add at least 1 uppercase letter.',
  needs_number: 'Add at least 1 number.',
  needs_special: 'Add at least 1 special character, such as ! or # (a space counts too).',
  personal: 'Don’t build your password from your name or email address. They are the first things someone would try.',
  repetitive: 'That password repeats itself or runs in order, like aaaa or 1234, so it is easy to guess. Choose another.',
  common: 'That password is one of the most common, or built from one, so it is easy to guess. Choose another.',
};

/** The hint under a field for a new password: the four requirements, in their order. */
export const NEW_PASSWORD_HINT = `Must include ${MIN_PASSWORD_LENGTH}+ characters, 1 uppercase letter, 1 number and 1 special character.`;

/** Only the letters and digits of a text, in lower case: "Riya.Sharma-2024!" is "riyasharma2024". */
export function lettersAndDigits(value: string): string {
  return normalizePassword(value)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

/**
 * The words a password must not be made of: the person's email address, its
 * parts, their name and its parts, and the app's name. Parts shorter than
 * three letters are left out, or "Al" would be taken out of "alligator".
 */
function personalWords(about: PasswordAbout): string[] {
  const words = new Set<string>();
  const add = (value: string | undefined) => {
    const word = value ? lettersAndDigits(value) : '';
    if (word.length >= 3) words.add(word);
  };
  const email = about.email?.trim().toLowerCase() ?? '';
  if (email) {
    const [local = '', domain = ''] = email.split('@');
    add(email);
    add(local);
    for (const part of local.split(/[._+-]+/)) add(part);
    add(domain.split('.')[0]);
  }
  for (const value of [about.name, about.appName]) {
    if (!value) continue;
    add(value);
    for (const part of value.split(/\s+/)) add(part);
  }
  // The longest first, so that "riyasharma" goes before "riya" can split it.
  return [...words].sort((a, b) => b.length - a.length);
}

/**
 * Built from personal words: one of them exactly, or fewer than six letters
 * and digits left once they are taken out ("riyasharma2024!"). A password
 * with no personal word in it is not personal, however few letters it has.
 */
function isPersonal(password: string, about: PasswordAbout): boolean {
  const words = personalWords(about);
  if (words.length === 0) return false;
  const plain = lettersAndDigits(password);
  if (words.includes(plain) || words.includes(normalizePassword(password).trim().toLowerCase())) return true;
  let rest = plain;
  for (const word of words) rest = rest.split(word).join('');
  return rest.length < plain.length && rest.length < 6;
}

/** Three characters or fewer, over and over; one short piece repeated ("abcabcabcabc"); or a run such as "abcdefghijkl" or "987654321098". */
function isRepetitive(password: string): boolean {
  const chars = Array.from(normalizePassword(password).toLowerCase());
  if (new Set(chars).size <= 3) return true;
  for (let size = 1; size <= chars.length / 2; size++) {
    if (chars.length % size !== 0) continue;
    const unit = chars.slice(0, size).join('');
    if (unit.repeat(chars.length / size) === chars.join('')) return true;
  }
  const run = Array.from(lettersAndDigits(password));
  if (run.length < chars.length - 2 || run.length < 8) return false;
  const step = (a: string, b: string): number => {
    // After 9 comes 0 on a keyboard's number row.
    if (a === '9' && b === '0') return 1;
    if (a === '0' && b === '9') return -1;
    return b.codePointAt(0)! - a.codePointAt(0)!;
  };
  const first = step(run[0]!, run[1]!);
  return Math.abs(first) === 1 && run.every((c, i) => i === 0 || step(run[i - 1]!, c) === first);
}

/** The shortest form of a password looked up in a list of common ones; the lists hold nothing shorter. */
export const COMMON_PASSWORD_MIN_LENGTH = 5;

/** Characters people put in place of letters: "p@ssw0rd" is "password". */
const SWAPPED: Record<string, string> = { '@': 'a', '4': 'a', '3': 'e', '1': 'i', '!': 'i', '0': 'o', '$': 's', '5': 's', '7': 't' };

/**
 * The forms of a password looked up in a list of common passwords, all in
 * lower case: as typed ("password@123"); its letters and digits
 * ("password123"), and those without a number at either end ("password");
 * without the digits and symbols around it ("p@ssw0rd" from "P@ssw0rd1!"),
 * and that with its letters swapped back ("password"); and its letters alone
 * ("monkey" from "Mon-key1!"). An 8-character password with an uppercase
 * letter, a number and a symbol is most often a common word with those
 * around it ("Monkey1!", "Summer@2026"), so the word is looked up too. Only
 * forms of COMMON_PASSWORD_MIN_LENGTH characters or more.
 */
export function commonPasswordForms(password: string): string[] {
  const typed = normalizePassword(password).trim().toLowerCase();
  const plain = lettersAndDigits(password);
  const word = plain.replace(/^\p{N}+|\p{N}+$/gu, '');
  const core = typed.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '');
  const unswapped = Array.from(core, (c) => SWAPPED[c] ?? c).join('');
  const letters = (value: string) => value.replace(/[^\p{L}]+/gu, '');
  const forms = new Set([typed, plain, word, core, unswapped, letters(typed), letters(unswapped)]);
  return [...forms].filter((form) => Array.from(form).length >= COMMON_PASSWORD_MIN_LENGTH);
}

let mostCommon: Set<string> | null = null;

/** One of the thousand most common passwords, or built from one (see commonPasswordForms). The server checks all 100,000. */
function isMostCommon(password: string): boolean {
  mostCommon ??= new Set(MOST_COMMON_PASSWORDS);
  return commonPasswordForms(password).some((form) => mostCommon!.has(form));
}

/**
 * The first thing wrong with a new password, or null: its length, then each
 * requirement it misses in the hint's order, then whether it is built from
 * `about`, repeats itself, or is one of the most common. The forms know only
 * the thousand most common passwords; the server checks the whole list too.
 */
export function passwordProblem(password: string, about: PasswordAbout = {}): PasswordProblem | null {
  const length = passwordLength(password);
  if (length < MIN_PASSWORD_LENGTH) return 'too_short';
  if (length > MAX_PASSWORD_LENGTH) return 'too_long';
  const missing = passwordRequirements(password).find((r) => !r.met);
  if (missing) return MISSING[missing.id];
  if (isPersonal(password, about)) return 'personal';
  if (isRepetitive(password)) return 'repetitive';
  if (isMostCommon(password)) return 'common';
  return null;
}

export type PasswordStrength = 'none' | 'very_weak' | 'weak' | 'good' | 'strong';

/** What the meter under a new password says. */
export const PASSWORD_STRENGTH_LABEL: Record<Exclude<PasswordStrength, 'none'>, string> = {
  very_weak: 'Very weak',
  weak: 'Weak',
  good: 'Good',
  strong: 'Strong',
};

/**
 * How strong a new password is, for the meter under its field: none while
 * the field is empty; very weak while it meets none of the four requirements
 * or one, and weak while it meets two or three, so that the bar moves as the
 * ticks do; good once it meets all four; strong with STRONG_PASSWORD_LENGTH
 * characters or more and lower case letters as well. One that would be
 * refused all the same (built from `about`, repeating itself, one of the
 * thousand most common, too long) is never better than weak. A password only
 * the server's longer list refuses still shows good or strong until the
 * server says so (see PasswordStrength in the app).
 */
export function passwordStrength(password: string, about: PasswordAbout = {}): PasswordStrength {
  if (password.length === 0) return 'none';
  const met = passwordRequirements(password).filter((r) => r.met).length;
  if (met < 2) return 'very_weak';
  if (met < REQUIREMENTS.length || passwordProblem(password, about)) return 'weak';
  return passwordLength(password) >= STRONG_PASSWORD_LENGTH && LOWERCASE.test(normalizePassword(password)) ? 'strong' : 'good';
}
